import { useState } from 'react';
import { ArrowLeft, Mail, Building2, CheckCircle2 } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLanguage } from '@/contexts/AppContext';

interface EmailVerificationPageProps {
  onBack: () => void;
  onVerificationComplete: (email: string, company: string) => void;
  currentEmail?: string;
  currentCompany?: string;
}

const translations = {
  ko: {
    title: '회사 이메일 인증',
    subtitle: '제휴 회사 직원 인증을 통해 특별 할인을 받으세요',
    emailLabel: '회사 이메일',
    emailPlaceholder: '예: user@samsung.com',
    sendCode: '인증 코드 발송',
    codeLabel: '인증 코드',
    codePlaceholder: '6자리 코드 입력',
    verify: '인증하기',
    supportedCompanies: '제휴 회사',
    currentVerification: '현재 인증 정보',
    employee: '직원 인증 완료',
    changeVerification: '인증 정보 변경',
    verified: '인증 완료',
    back: '뒤로'
  },
  es: {
    title: 'Verificación de Email Corporativo',
    subtitle: 'Obtén descuentos especiales verificando tu email corporativo',
    emailLabel: 'Email Corporativo',
    emailPlaceholder: 'Ej: usuario@samsung.com',
    sendCode: 'Enviar Código',
    codeLabel: 'Código de Verificación',
    codePlaceholder: 'Ingresa código de 6 dígitos',
    verify: 'Verificar',
    supportedCompanies: 'Empresas Afiliadas',
    currentVerification: 'Verificación Actual',
    employee: 'empleado verificado',
    changeVerification: 'Cambiar Verificación',
    verified: 'Verificado',
    back: 'Atrás'
  },
  en: {
    title: 'Company Email Verification',
    subtitle: 'Get special discounts by verifying your company email',
    emailLabel: 'Company Email',
    emailPlaceholder: 'e.g., user@samsung.com',
    sendCode: 'Send Code',
    codeLabel: 'Verification Code',
    codePlaceholder: 'Enter 6-digit code',
    verify: 'Verify',
    supportedCompanies: 'Partner Companies',
    currentVerification: 'Current Verification',
    employee: 'employee verified',
    changeVerification: 'Change Verification',
    verified: 'Verified',
    back: 'Back'
  }
};

export function EmailVerificationPage({
  onBack,
  onVerificationComplete,
  currentEmail,
  currentCompany,
}: EmailVerificationPageProps) {
  const { language } = useLanguage();
  const [email, setEmail] = useState(currentEmail || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [isEditing, setIsEditing] = useState(!currentEmail);

  const t = translations[language];

  const handleSendVerificationCode = () => {
    if (email) {
      setShowVerificationInput(true);
    }
  };

  const handleVerifyEmail = () => {
    if (verificationCode.length === 6) {
      const emailDomain = email.split('@')[1]?.toLowerCase();
      let company = '';
      
      if (emailDomain?.includes('samsung')) {
        company = 'samsung';
      } else if (emailDomain?.includes('kakao')) {
        company = 'kakao';
      } else if (emailDomain?.includes('naver')) {
        company = 'naver';
      }

      if (company) {
        onVerificationComplete(email, company);
      }
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-gradient-to-r from-secondary to-secondary/70 sticky top-0 z-50">
        <div className="flex items-center gap-4 p-[min(5vw,1.25rem)]">
          <button
            onClick={onBack}
            className="p-2 hover:bg-white/10 rounded-xl transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>
          <h1 className="text-[calc(1.125rem+0.25vw)] font-bold text-white flex-1">
            {t.title}
          </h1>
        </div>
      </div>

      <div className="p-[min(5vw,1.25rem)] pb-[min(8vw,2rem)]">
        {/* Subtitle */}
        <p className="text-[calc(0.875rem+0.125vw)] text-muted-foreground mb-[min(6vw,2rem)] text-center">
          {t.subtitle}
        </p>

        {/* Current Verification (if exists) */}
        {currentEmail && !isEditing && (
          <Card className="p-[min(5vw,1.75rem)] bg-background rounded-3xl shadow-lg mb-[min(5vw,1.5rem)] border-2 border-secondary/20">
            <div className="flex items-center gap-3 mb-[min(4vw,1.5rem)]">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-primary to-primary-hover">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-[calc(1rem+0.25vw)] font-bold text-foreground">
                  {t.currentVerification}
                </h3>
                <div className="flex items-center gap-1.5 mt-1">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  <span className="text-[calc(0.75rem+0.125vw)] text-primary font-medium">
                    {t.verified}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-accent/20 to-primary/10 rounded-2xl p-4 flex items-center gap-3 mb-4">
              <CheckCircle2 className="h-8 w-8 text-primary shrink-0" />
              <div className="flex-1">
                <p className="text-[calc(0.875rem+0.25vw)] font-medium text-foreground">
                  {currentEmail}
                </p>
                <p className="text-[calc(0.7rem+0.125vw)] text-muted-foreground capitalize">
                  {currentCompany} {t.employee}
                </p>
              </div>
            </div>

            <Button
              onClick={() => setIsEditing(true)}
              className="w-full bg-gradient-to-r from-secondary to-secondary/70 hover:from-secondary-hover hover:to-secondary/60 text-white rounded-xl py-3"
            >
              {t.changeVerification}
            </Button>
          </Card>
        )}

        {/* Verification Form */}
        {(!currentEmail || isEditing) && (
          <Card className="p-[min(5vw,1.75rem)] bg-background rounded-3xl shadow-lg mb-[min(5vw,1.5rem)] border-2 border-secondary/20">
            <div className="flex items-center gap-3 mb-[min(4vw,1.5rem)]">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-secondary to-secondary/70">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-[calc(1rem+0.25vw)] font-bold text-foreground">
                  {t.emailLabel}
                </h3>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[calc(0.875rem+0.125vw)] text-muted-foreground mb-2">
                  {t.emailLabel}
                </label>
                <Input
                  type="email"
                  placeholder={t.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl py-6 px-4 bg-muted border-0 text-[calc(0.875rem+0.125vw)]"
                />
              </div>
              
              {!showVerificationInput ? (
                <Button
                  onClick={handleSendVerificationCode}
                  disabled={!email}
                  className="w-full bg-gradient-to-r from-secondary to-secondary/70 hover:from-secondary-hover hover:to-secondary/60 text-white rounded-xl py-6 disabled:opacity-50"
                >
                  {t.sendCode}
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[calc(0.875rem+0.125vw)] text-muted-foreground mb-2">
                      {t.codeLabel}
                    </label>
                    <Input
                      type="text"
                      placeholder={t.codePlaceholder}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.slice(0, 6))}
                      maxLength={6}
                      className="rounded-xl py-6 px-4 bg-muted border-0 text-center text-2xl font-bold tracking-widest"
                    />
                  </div>
                  <Button
                    onClick={handleVerifyEmail}
                    disabled={verificationCode.length !== 6}
                    className="w-full bg-gradient-to-r from-primary to-primary-hover hover:from-primary-hover hover:to-primary text-white rounded-xl py-6 disabled:opacity-50"
                  >
                    {t.verify}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Supported Companies */}
        <Card className="p-[min(5vw,1.75rem)] bg-background rounded-3xl shadow-lg border-2 border-border/50">
          <h3 className="text-[calc(1rem+0.25vw)] font-bold text-foreground mb-4">
            {t.supportedCompanies}
          </h3>
          <div className="flex gap-3 flex-wrap">
            {['Samsung', 'Kakao', 'Naver'].map((company) => (
              <Badge
                key={company}
                className="bg-gradient-to-r from-secondary/10 to-secondary/5 text-secondary border border-secondary/30 rounded-xl px-4 py-2"
              >
                <Building2 className="h-4 w-4 mr-2" />
                {company}
              </Badge>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}